import { Box, SxProps, Theme, useTheme } from '@mui/material'
import { OptimizerEvents2 } from '../types/ports'
import { DateTime } from 'luxon'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { callAxios } from '../hooks/useAxios'

/**
 * Vertical "SoC river" plan view for Battery Optimizer 2.0 front-page widget.
 *
 * Time flows top→bottom. Each plan slot becomes an SVG trapezoid whose right
 * edge is the SoC trajectory (as a fraction of 0–100%) and whose fill color
 * encodes ΔSoC per hour (violet = charging, cream = idle, orange = discharging).
 *
 * Timestamps and state labels are rendered only at state-change boundaries,
 * with a lean horizontal rule between groups. There are no visible column
 * dividers — the columns (time | state | river | unused) are virtual.
 */

export interface BatterySchedule2Props {
  events: OptimizerEvents2
  socPct: number[]
  currentSoc?: number | null
  // Port id used to pull device-reported FCR constraints from the server.
  // Pass the BATTERY_OPTIMIZER2 port id; leave undefined to skip the fetch.
  portId?: number | null
  sx?: SxProps<Theme>
}

interface FcrConstraints {
  time: string
  up: number[]
  down: number[]
}

// Layout constants (all in svg viewBox units; component scales to container width).
const PX_PER_15MIN = 12
const TOTAL_WIDTH_PX = 300
const LABEL_FONT = 8

// Collapsed view: show 5 hours by default; the final hour (4h..5h) fades to
// the widget background color so the reader sees there's more below.
const COLLAPSED_HOURS = 5
const FADE_START_HOURS = 4
const COLLAPSED_MAX_Y = COLLAPSED_HOURS * 4 * PX_PER_15MIN
const FADE_START_Y = FADE_START_HOURS * 4 * PX_PER_15MIN

// Virtual columns. Time + state share the left side; the river is a narrow
// strip in the middle; FCR constraint indicators sit to the right.
const TIME_X = 4
const STATE_X = 27
const RIVER_LEFT_PX = 86
const RIVER_RIGHT_PX = 142
// Down / up constraint indicator columns (triangle + kW label).
const DOWN_TRI_X = 158
const DOWN_TEXT_X = 164
const UP_TRI_X = 210
const UP_TEXT_X = 216
const DOWN_COLOR = '#8338e8'
const UP_COLOR = '#fdd835'

// Three-stop color ramp: full-discharge, idle, full-charge. When tuning via
// ?tune=1, the pickers mutate these runtime values; copy the chosen hex
// triple back into DEFAULT_STOPS when you're happy and strip the tuner.
const DEFAULT_STOPS = {
  down: '#6c0fb6',
  mid: '#4f4d53',
  up: '#f6d32d',
}

function hexToRgb(hex: string): [number, number, number] {
  const m = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)
  return m
    ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
    : [0, 0, 0]
}

function makeDeltaColor(stops: typeof DEFAULT_STOPS) {
  const down = hexToRgb(stops.down)
  const mid = hexToRgb(stops.mid)
  const up = hexToRgb(stops.up)
  return (deltaPctPerHour: number): string => {
    const clamped = Math.max(-20, Math.min(20, deltaPctPerHour))
    const [lo, hi, t] =
      clamped <= 0
        ? [down, mid, (clamped + 20) / 20]
        : [mid, up, clamped / 20]
    const r = Math.round(lo[0] + t * (hi[0] - lo[0]))
    const g = Math.round(lo[1] + t * (hi[1] - lo[1]))
    const b = Math.round(lo[2] + t * (hi[2] - lo[2]))
    return `rgb(${r}, ${g}, ${b})`
  }
}

function socToX(pct: number): number {
  const width = RIVER_RIGHT_PX - RIVER_LEFT_PX
  const clamped = Math.max(0, Math.min(100, pct))
  return RIVER_LEFT_PX + (clamped / 100) * width
}

export function BatterySchedule2(props: BatterySchedule2Props): React.ReactElement {
  const { events, socPct, currentSoc, portId, sx } = props
  const theme = useTheme()
  const { t } = useTranslation()
  const [constraints, setConstraints] = useState<FcrConstraints | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [stops, setStops] = useState(DEFAULT_STOPS)
  const deltaColor = useMemo(() => makeDeltaColor(stops), [stops])
  const showTuner = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('tune') === '1'

  // Refetch constraints whenever the plan's first timestamp changes (≈ new
  // base_ts from the optimizer) or on mount. One endpoint call per hour per
  // viewer in the worst case.
  const refetchKey = events[0]?.[0] ?? ''
  useEffect(() => {
    if (!portId) return
    let cancelled = false
    callAxios<FcrConstraints | null>({ url: `/constraints/${portId}` }).then(res => {
      if (cancelled) return
      if (res.success && res.data) {
        setConstraints(res.data)
      } else {
        setConstraints(null)
      }
    })
    return () => { cancelled = true }
  }, [portId, refetchKey])

  // Zip events with soc values; ignore any trailing mismatch. The two come
  // from different ports (0x222 / 0x223) and reach the browser as separate
  // websocket updates, so a render with events but no SoC values yet is
  // normal. Everything below indexes the zipped slots, so that is what the
  // empty state has to be decided on.
  const n = Math.min(events.length, socPct.length)
  if (n === 0) {
    return <Box sx={sx}><Trans>No plan</Trans></Box>
  }

  const slots = events.slice(0, n).map(([ts, ev], i) => ({
    ts,
    state: ev.state,
    duration_s: ev.duration_s,
    is_user_override: ev.is_user_override,
    soc_end: socPct[i],
  }))

  // Starting SoC for slot 0: prefer the live battery SoC if we have it,
  // otherwise fall back to the end-of-slot-0 value so slot 0 is flat.
  const startSoc =
    typeof currentSoc === 'number' && !isNaN(currentSoc)
      ? Math.max(0, Math.min(100, currentSoc))
      : slots[0]?.soc_end ?? 0

  // Compute slot Y positions.
  const slotYs: { top: number, bot: number }[] = []
  let y = 0
  for (const s of slots) {
    const h = (s.duration_s / 900) * PX_PER_15MIN
    slotYs.push({ top: y, bot: y + h })
    y += h
  }
  const fullH = y

  // Collapsed view: only keep slots that start before the 5h mark. The last
  // kept slot may extend past it — the fade overlay hides that tail.
  const canCollapse = fullH > COLLAPSED_MAX_Y
  const isCollapsed = canCollapse && !expanded
  const visibleSlotCount = isCollapsed
    ? Math.max(1, slotYs.findIndex(s => s.top >= COLLAPSED_MAX_Y))
    : slots.length
  const totalH = isCollapsed ? COLLAPSED_MAX_Y : fullH

  // Knot points of the SoC trajectory: one per slot boundary, in viewBox
  // coordinates. knots[0] is the top of slot 0 (starting SoC), knots[i] is
  // the bottom of slot i-1 / top of slot i.
  const knots: { x: number, y: number }[] = [{ x: socToX(startSoc), y: 0 }]
  for (let i = 0; i < slots.length; i++) {
    knots.push({ x: socToX(slots[i].soc_end), y: slotYs[i].bot })
  }

  // Catmull-Rom → cubic bezier control points for the segment knots[i]→knots[i+1].
  // Yields C1 continuity across segments so adjacent slots' right edges flow
  // without visible corners.
  function controlPoints(i: number): { cp1: { x: number, y: number }, cp2: { x: number, y: number } } {
    const p0 = knots[Math.max(0, i - 1)]
    const p1 = knots[i]
    const p2 = knots[i + 1]
    const p3 = knots[Math.min(knots.length - 1, i + 2)]
    return {
      cp1: { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 },
      cp2: { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 },
    }
  }

  // Build per-slot trapezoid paths. Right edge is a cubic bezier for
  // smoothing; bottom edge is overdrawn by 0.5 to eliminate sub-pixel gaps
  // between adjacent slots.
  const trapezoids = slots.slice(0, visibleSlotCount).map((s, i) => {
    const prev = i === 0 ? startSoc : slots[i - 1].soc_end
    const next = s.soc_end
    const durH = s.duration_s / 3600
    const deltaPctPerHour = durH > 0 ? (next - prev) / durH : 0
    const fill = deltaColor(deltaPctPerHour)
    const y1 = slotYs[i].top
    const y2 = slotYs[i].bot + 0.5
    const xLeft = RIVER_LEFT_PX
    const top = knots[i]
    const bot = knots[i + 1]
    const { cp1, cp2 } = controlPoints(i)
    const d =
      `M ${xLeft},${y1} ` +
      `L ${top.x},${y1} ` +
      `C ${cp1.x},${cp1.y} ${cp2.x},${cp2.y} ${bot.x},${y2} ` +
      `L ${xLeft},${y2} Z`
    return (
      <path
        key={i}
        d={d}
        fill={fill}
        stroke={s.is_user_override ? '#ff2ea8' : fill}
        strokeWidth={s.is_user_override ? 1.5 : 0.5}
        strokeDasharray={s.is_user_override ? '3,2' : undefined}
      />
    )
  })

  // State-change boundaries (constraints live in their own per-hour column
  // and don't force a split here).
  const transitions: number[] = []
  for (let i = 0; i < visibleSlotCount; i++) {
    if (i === 0 || slots[i].state !== slots[i - 1].state) {
      transitions.push(i)
    }
  }

  const ruleColor = 'rgba(255,255,255,0.14)'

  // Separators between state rows live only inside the state-name column —
  // they don't cross the SoC river or the constraint indicators.
  const rules = transitions.slice(1).map(i => {
    const y1 = slotYs[i].top
    return (
      <line
        key={`r${i}`}
        x1={STATE_X}
        x2={RIVER_LEFT_PX - 4}
        y1={y1}
        y2={y1}
        stroke={ruleColor}
        strokeWidth={1}
      />
    )
  })

  // Hour bands aligned to real wall-clock hours. Plans are 15-min aligned
  // (no longer guaranteed to start on the hour), so the first band may be a
  // partial hour up to the next :00 boundary and the last band may be cut off
  // by the collapsed/expanded height. Time→Y is linear (every 15 min =
  // PX_PER_15MIN), so hour boundaries can be placed directly from timestamps.
  const baseLocal = DateTime.fromISO(slots[0].ts).toLocal()
  const baseEpoch = baseLocal.toSeconds()
  const timeToY = (epochSec: number): number => ((epochSec - baseEpoch) / 900) * PX_PER_15MIN
  const planEndEpoch = baseEpoch + (totalH / PX_PER_15MIN) * 900

  const bandSegments: { y1: number, y2: number, startDt: DateTime, hourStartSec: number, hour: number }[] = []
  let segDt = baseLocal
  while (segDt.toSeconds() < planEndEpoch - 1e-6) {
    const nextBoundary = segDt.startOf('hour').plus({ hours: 1 })
    const segEndEpoch = Math.min(nextBoundary.toSeconds(), planEndEpoch)
    bandSegments.push({
      y1: timeToY(segDt.toSeconds()),
      y2: timeToY(segEndEpoch),
      startDt: segDt,
      hourStartSec: Math.floor(segDt.startOf('hour').toSeconds()),
      hour: segDt.hour,
    })
    segDt = nextBoundary
  }

  // Alternating background, shaded on odd wall-clock hours so the striping is
  // stable across replans regardless of where the plan starts.
  const hourBands = bandSegments.map((b, i) => (
    <rect
      key={`hb${i}`}
      x={0}
      y={b.y1}
      width={TOTAL_WIDTH_PX}
      height={b.y2 - b.y1}
      fill={b.hour % 2 === 1 ? 'rgba(255,255,255,0.04)' : 'transparent'}
    />
  ))

  // Timestamp at the top of each band. The first band shows the plan's actual
  // (15-min aligned) start; every later band starts on the hour, so :00.
  const hourLabels = bandSegments.map((b, i) => {
    const baseline = b.y1 + LABEL_FONT + 1
    return (
      <text
        key={`ht${i}`}
        x={TIME_X}
        y={baseline}
        fill="rgba(255,255,255,0.7)"
        fontSize={LABEL_FONT}
        fontFamily="inherit"
      >
        {b.startDt.toFormat('HH:mm')}
      </text>
    )
  })

  // Per-hour FCR constraint indicators, vertically centered in their band.
  // Constraints are hourly by definition so we render them once per band
  // rather than once per state-change row.
  const constraintBaseSec = constraints
    ? Math.floor(DateTime.fromISO(constraints.time).toSeconds())
    : null
  function constraintForHour(hourStartSec: number): { up: number, down: number } | null {
    if (!constraints || constraintBaseSec === null) return null
    const idx = Math.round((hourStartSec - constraintBaseSec) / 3600)
    if (idx < 0 || idx >= constraints.up.length || idx >= constraints.down.length) return null
    return { up: constraints.up[idx], down: constraints.down[idx] }
  }
  const constraintIndicators = bandSegments.map((b, i) => {
    const c = constraintForHour(b.hourStartSec)
    if (!c) return null
    const down = c.down > 0 ? c.down : null
    const up = c.up > 0 ? c.up : null
    if (down === null && up === null) return null
    const cy = (b.y1 + b.y2) / 2
    return (
      <g key={`cb${i}`}>
        {down !== null && (
          <>
            <polygon
              points={`${DOWN_TRI_X - 2.5},${cy - 2.5} ${DOWN_TRI_X + 2.5},${cy - 2.5} ${DOWN_TRI_X},${cy + 2.5}`}
              fill={DOWN_COLOR}
            />
            <text
              x={DOWN_TEXT_X}
              y={cy}
              dominantBaseline="middle"
              fill={DOWN_COLOR}
              fontSize={LABEL_FONT}
              fontFamily="inherit"
            >
              {(down / 1000).toFixed(1)}
            </text>
          </>
        )}
        {up !== null && (
          <>
            <polygon
              points={`${UP_TRI_X - 2.5},${cy + 2.5} ${UP_TRI_X + 2.5},${cy + 2.5} ${UP_TRI_X},${cy - 2.5}`}
              fill={UP_COLOR}
            />
            <text
              x={UP_TEXT_X}
              y={cy}
              dominantBaseline="middle"
              fill={UP_COLOR}
              fontSize={LABEL_FONT}
              fontFamily="inherit"
            >
              {(up / 1000).toFixed(1)}
            </text>
          </>
        )}
      </g>
    )
  })

  // Single SoC guide at 100% — marks the right edge of the river.
  const guideColor = 'rgba(255,255,255,0.08)'
  const guides = [100].map(pct => (
    <line
      key={`g${pct}`}
      x1={socToX(pct)}
      x2={socToX(pct)}
      y1={0}
      y2={totalH}
      stroke={guideColor}
      strokeWidth={1}
    />
  ))

  // State name at each state-change boundary. Timestamps live in their own
  // hourly column (see hourLabels) and constraints in their own per-hour
  // column (see constraintIndicators), so state rows hold nothing else.
  const labels = transitions.map(i => {
    const s = slots[i]
    const y1 = slotYs[i].top
    const stateLabel = t(`BatoptState-${s.state.replace('BATOPT_STATE_', '')}`)
    const baseline = y1 + LABEL_FONT + 1
    return (
      <text
        key={`l${i}`}
        x={STATE_X}
        y={baseline}
        fill={theme.palette.primary.main}
        fontSize={LABEL_FONT}
        fontFamily="inherit"
      >
        {stateLabel}
        {s.is_user_override && (
          <tspan fill="#ff2ea8" dx={3}>*</tspan>
        )}
      </text>
    )
  })

  // Axis ticks at the bottom — only under the river.
  const axisY = totalH + 10
  const bgColor = theme.palette.statsBackground.main

  return (
    <Box sx={sx}>
      {/* Legend row. Same viewBox width + 100% width as the plan svg below, so
          column titles line up with their columns. The time column (far left)
          intentionally has no title. */}
      <svg
        width="100%"
        viewBox={`0 0 ${TOTAL_WIDTH_PX} 11`}
        preserveAspectRatio="xMidYMin meet"
        style={{ display: 'block' }}
      >
        <text x={STATE_X} y={8} fill="rgba(255,255,255,0.55)" fontSize={7} fontFamily="inherit">
          {t('Batopt2Legend-mode')}
        </text>
        <text
          x={(RIVER_LEFT_PX + RIVER_RIGHT_PX) / 2}
          y={8}
          textAnchor="middle"
          fill="rgba(255,255,255,0.55)"
          fontSize={7}
          fontFamily="inherit"
        >
          {t('Batopt2Legend-soc')}
        </text>
        <text
          x={(DOWN_TRI_X + UP_TEXT_X) / 2}
          y={8}
          textAnchor="middle"
          fill="rgba(255,255,255,0.55)"
          fontSize={7}
          fontFamily="inherit"
        >
          {t('Batopt2Legend-reserve')}
        </text>
      </svg>
      <svg
        width="100%"
        viewBox={`0 0 ${TOTAL_WIDTH_PX} ${totalH + 16}`}
        preserveAspectRatio="xMidYMin meet"
        shapeRendering="geometricPrecision"
        style={{ display: 'block' }}
      >
        {isCollapsed && (
          <defs>
            <linearGradient id="batschedFade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={bgColor} stopOpacity={0} />
              <stop offset="100%" stopColor={bgColor} stopOpacity={1} />
            </linearGradient>
          </defs>
        )}
        {hourBands}
        {guides}
        {trapezoids}
        {rules}
        {hourLabels}
        {labels}
        {constraintIndicators}
        {isCollapsed && (
          <rect
            x={0}
            y={FADE_START_Y}
            width={TOTAL_WIDTH_PX}
            height={COLLAPSED_MAX_Y - FADE_START_Y}
            fill="url(#batschedFade)"
          />
        )}
        {/* Axis */}
        <text x={socToX(0)} y={axisY} fontSize={6} fill="rgba(255,255,255,0.4)" textAnchor="middle">0%</text>
        <text x={socToX(50)} y={axisY} fontSize={6} fill="rgba(255,255,255,0.4)" textAnchor="middle">50%</text>
        <text x={socToX(100)} y={axisY} fontSize={6} fill="rgba(255,255,255,0.4)" textAnchor="middle">100%</text>
      </svg>
      {canCollapse && (
        <Box
          sx={{
            mt: 0.5,
            color: theme.palette.primary.main,
            cursor: 'pointer',
            fontSize: '85%',
            textAlign: 'center',
          }}
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? <Trans>(less)</Trans> : <Trans>(more)</Trans>}
        </Box>
      )}
      {showTuner && (
        <Box sx={{ mt: 1, fontSize: '75%', display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
          {(['down', 'mid', 'up'] as const).map(key => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {key}
              <input
                type="color"
                value={stops[key]}
                onChange={e => setStops(s => ({ ...s, [key]: e.target.value }))}
                style={{ width: 24, height: 20, border: 0, padding: 0 }}
              />
            </label>
          ))}
          <code style={{ fontSize: '85%' }}>
            {stops.down} / {stops.mid} / {stops.up}
          </code>
        </Box>
      )}
    </Box>
  )
}
